import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Fab,
  Modal,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useNavigate } from "react-router-dom";
import {
  getInvoicesList,
  deleteInvoice,
  getInvoiceById,
} from "../DatabaseApi/DatabaseApi1";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// MODIFICATION: Corrected the import path from ../ to ./
import { useUserContext } from "./context/UserContext";

// --- Helper Functions ---

const formatAddress = (address) => {
  if (!address) return "N/A";
  if (typeof address === "string") return address;
  return [
    address.name || "",
    address.street || "",
    [address.city, address.state].filter(Boolean).join(", "),
    address.zip || "",
    address.country || "",
  ]
    .filter(Boolean)
    .join("\n");
};

const getShortAddress = (address) => {
  if (!address) return "N/A";
  if (typeof address === "string") return address.split("\n")[0];
  return address.name || address.street || address.city || "N/A";
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "N/A";
  }
};

const formatCurrency = (amount, symbol = "₹") => {
  const num = parseFloat(String(amount).replace(/[^0-9.-]+/g, "")) || 0;
  return `${symbol} ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeInvoiceData = (invoice) => {
  if (!invoice) return {};

  const normalized = {
    id: invoice.id,
    templateId: invoice.templateId || "template4",

    invoiceNumber:
      invoice.invoiceNumber ||
      invoice.proformaInvoiceNumber ||
      invoice.taxInvoiceNumber ||
      invoice.id,

    billTo:
      invoice.billTo ||
      invoice.invoiceTo ||
      invoice.customerName ||
      invoice.shipTo,

    shipTo: invoice.shipTo || invoice.billTo || invoice.invoiceTo,

    invoiceDate:
      invoice.invoiceDate ||
      invoice.proformaInvoiceDate ||
      invoice.taxInvoiceDate ||
      invoice.date,

    dueDate:
      invoice.dueDate || invoice.proformaDueDate || invoice.taxDueDate || null,

    total:
      invoice.total ||
      invoice.proformaTotal ||
      invoice.taxTotal ||
      invoice.totalAmount ||
      invoice.amount ||
      0,

    subtotal:
      invoice.subtotal || invoice.proformaSubtotal || invoice.taxSubtotal || 0,

    Gst:
      invoice.Gst ||
      invoice.proformaGst ||
      invoice.taxGst ||
      invoice.gstAmount ||
      0,

    items: invoice.items || invoice.products || [],

    termsAndConditions:
      invoice.termsAndConditions ||
      invoice.proformaTermsAndConditions ||
      invoice.notes ||
      "",

    invoiceType:
      invoice.invoiceType ||
      (invoice.proformaInvoiceNumber ? "proforma" : "tax"),
  };

  return normalized;
};

// --- Main Component ---

export default function InvoiceList() {
  const navigate = useNavigate();
  const { invoiceCurrency } = useUserContext();
  const currencySymbol = invoiceCurrency?.symbol || "₹";

  const [activeTab, setActiveTab] = useState(0);
  const [invoices, setInvoices] = useState({ tax: [], proforma: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const [taxResult, proformaResult] = await Promise.all([
          getInvoicesList("tax"),
          getInvoicesList("proforma"),
        ]);

        setInvoices({
          tax: (taxResult || []).map(normalizeInvoiceData),
          proforma: (proformaResult || []).map(normalizeInvoiceData),
        });
      } catch (err) {
        console.error("Error loading invoices:", err);
        setError("Failed to load invoices. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  // --- Action Handlers ---

  const handleNavigate = () => navigate("/invoice-templates");
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleView = async (invoiceId) => {
    try {
      const fullInvoice = await getInvoiceById(invoiceId);
      if (fullInvoice) {
        setSelectedInvoice(normalizeInvoiceData(fullInvoice));
        setViewModalOpen(true);
      } else {
        alert("Could not find invoice details.");
      }
    } catch (err) {
      console.error("Error fetching invoice details:", err);
      alert("Error fetching invoice details. Please try again.");
    }
  };

  const handleDownload = async (invoiceId) => {
    try {
      const fullInvoice = await getInvoiceById(invoiceId);
      if (fullInvoice) {
        generatePDF(normalizeInvoiceData(fullInvoice), currencySymbol);
      }
    } catch (err) {
      console.error("Error generating PDF:", err);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handleEdit = (invoice) => {
    navigate(
      `/edit-invoice/${invoice.templateId || "template4"}/${invoice.id}`
    );
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        const success = await deleteInvoice(id);
        if (success) {
          setInvoices((prev) => ({
            tax: prev.tax.filter((inv) => inv.id !== id),
            proforma: prev.proforma.filter((inv) => inv.id !== id),
          }));
          alert("Invoice deleted successfully!");
        }
      } catch (err) {
        console.error("Error deleting invoice:", err);
        alert("Error deleting invoice. Please try again.");
      }
    }
  };

  // --- PDF Generation ---

  const generatePDF = (invoice, symbol) => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = 20;

      pdf.setFontSize(18);
      pdf.setFont("helvetica", "bold");
      pdf.text(
        `${invoice.invoiceType === "proforma" ? "PROFORMA " : ""}INVOICE`,
        pageWidth / 2,
        yPos,
        { align: "center" }
      );
      yPos += 15;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      const billToAddress = formatAddress(invoice.billTo);
      const shipToAddress = formatAddress(invoice.shipTo);
      pdf.text(`Invoice ID: ${invoice.invoiceNumber}`, margin, yPos);
      pdf.text(
        `Invoice Date: ${formatDate(invoice.invoiceDate)}`,
        margin,
        yPos + 7
      );
      pdf.text(`Due Date: ${formatDate(invoice.dueDate)}`, margin, yPos + 14);
      pdf.text("Bill To:", pageWidth - margin, yPos, { align: "right" });
      pdf.text(billToAddress, pageWidth - margin, yPos + 5, { align: "right" });
      pdf.text("Ship To:", pageWidth - margin, yPos + 20, { align: "right" });
      pdf.text(shipToAddress, pageWidth - margin, yPos + 25, {
        align: "right",
      });
      yPos += 45;

      const headers = [["Description", "Qty", "Price", "Amount"]];
      const items = (invoice.items || []).map((item) => [
        item.item || "N/A",
        item.quantity || 1,
        formatCurrency(item.unitPrice || 0, symbol),
        formatCurrency(item.amount || 0, symbol),
      ]);

      autoTable(pdf, {
        startY: yPos,
        head: headers,
        body: items,
        theme: "grid",
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 3 },
      });

      yPos = pdf.lastAutoTable.finalY + 15;

      const summaryX = pageWidth - margin;
      pdf.text(
        `Subtotal: ${formatCurrency(invoice.subtotal, symbol)}`,
        summaryX,
        yPos,
        { align: "right" }
      );
      pdf.text(
        `GST: ${formatCurrency(invoice.Gst || 0, symbol)}`,
        summaryX,
        yPos + 7,
        {
          align: "right",
        }
      );
      pdf.setFont("helvetica", "bold");
      pdf.text(
        `Total: ${formatCurrency(invoice.total, symbol)}`,
        summaryX,
        yPos + 14,
        {
          align: "right",
        }
      );

      yPos = pdf.internal.pageSize.getHeight() - 20;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.text("Thank you for your business!", pageWidth / 2, yPos, {
        align: "center",
      });

      pdf.save(`Invoice_${invoice.invoiceNumber}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      alert("An error occurred while generating the PDF.");
    }
  };

  // --- Render Methods ---

  const renderActions = (invoice, isProforma) => (
    <Box display="flex" justifyContent="center" gap={0.5}>
      <Tooltip title="View Details">
        <IconButton
          size="small"
          color="default"
          onClick={() => handleView(invoice.id)}
        >
          <VisibilityIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download PDF">
        <IconButton
          size="small"
          color="primary"
          onClick={() => handleDownload(invoice.id)}
        >
          <GetAppIcon />
        </IconButton>
      </Tooltip>
      {isProforma && (
        <Tooltip title="Edit Invoice">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleEdit(invoice)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Delete Invoice">
        <IconButton
          size="small"
          color="error"
          onClick={() => handleDelete(invoice.id)}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const renderInvoiceTable = (data, isProforma = false) => (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ overflow: "auto" }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {[
              "Invoice ID",
              "Bill To",
              "Invoice Date",
              "Ship To",
              "Total Amount",
              "Due Date",
              "Actions",
            ].map((headCell, index) => (
              <TableCell
                key={headCell}
                align={index === 6 ? "center" : "left"}
                sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
              >
                {headCell}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                <CircularProgress />
                <Typography>Loading invoices...</Typography>
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                <Typography>No invoices found.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map((invoice) => (
              <TableRow
                key={invoice.id}
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{getShortAddress(invoice.billTo)}</TableCell>
                <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                <TableCell>{getShortAddress(invoice.shipTo)}</TableCell>
                <TableCell>
                  {formatCurrency(invoice.total, currencySymbol)}
                </TableCell>
                <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                <TableCell align="center">
                  {renderActions(invoice, isProforma)}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderInvoiceCards = (data, isProforma = false) => (
    <Grid container spacing={3}>
      {loading ? (
        <Grid item xs={12} sx={{ textAlign: "center", py: 5 }}>
          <CircularProgress />
          <Typography>Loading invoices...</Typography>
        </Grid>
      ) : data.length === 0 ? (
        <Grid item xs={12} sx={{ textAlign: "center", py: 5 }}>
          <Typography>No invoices found.</Typography>
        </Grid>
      ) : (
        data.map((invoice) => (
          <Grid item xs={12} sm={6} md={4} key={invoice.id}>
            <Card
              elevation={2}
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="div" noWrap>
                  {invoice.invoiceNumber}
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  Bill To: {getShortAddress(invoice.billTo)}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2">
                  <strong>Total:</strong>{" "}
                  {formatCurrency(invoice.total, currencySymbol)}
                </Typography>
                <Typography variant="body2">
                  <strong>Due Date:</strong> {formatDate(invoice.dueDate)}
                </Typography>
              </CardContent>
              <CardActions
                sx={{
                  justifyContent: "center",
                  backgroundColor: "action.hover",
                }}
              >
                {renderActions(invoice, isProforma)}
              </CardActions>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  const renderInvoiceDetailModal = () => (
    <Modal open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", md: "70%" },
          maxWidth: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <IconButton
          onClick={() => setViewModalOpen(false)}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        {selectedInvoice && (
          <>
            <Typography variant="h5" gutterBottom>
              Invoice: {selectedInvoice.invoiceNumber}
            </Typography>
            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" fontSize="1rem">
                  Bill To:
                </Typography>
                <Typography sx={{ whiteSpace: "pre-line" }}>
                  {formatAddress(selectedInvoice.billTo)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h6" fontSize="1rem">
                  Ship To:
                </Typography>
                <Typography sx={{ whiteSpace: "pre-line" }}>
                  {formatAddress(selectedInvoice.shipTo)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <strong>Date:</strong> {formatDate(selectedInvoice.invoiceDate)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <strong>Due Date:</strong> {formatDate(selectedInvoice.dueDate)}
              </Grid>
            </Grid>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selectedInvoice.items || []).map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.item || "N/A"}</TableCell>
                      <TableCell align="right">{item.quantity || 1}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.unitPrice, currencySymbol)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.amount, currencySymbol)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid container>
              <Grid item xs={6}>
                <Typography variant="h6">Notes</Typography>
                <Typography sx={{ fontStyle: "italic" }}>
                  {selectedInvoice.termsAndConditions || "No additional notes."}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography>
                  Subtotal:{" "}
                  {formatCurrency(selectedInvoice.subtotal, currencySymbol)}
                </Typography>
                <Typography>
                  GST:{" "}
                  {formatCurrency(selectedInvoice.Gst || 0, currencySymbol)}
                </Typography>
                <Typography variant="h6">
                  Total: {formatCurrency(selectedInvoice.total, currencySymbol)}
                </Typography>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => handleDownload(selectedInvoice.id)}
                startIcon={<GetAppIcon />}
              >
                Download PDF
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );

  return (
    <div>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            <Typography variant="h4" component="h1">
              Invoice List
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="View mode"
              >
                <ToggleButton value="list" aria-label="list view">
                  <ViewListIcon />
                </ToggleButton>
                <ToggleButton value="card" aria-label="card view">
                  <ViewModuleIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Tax Invoices" />
                <Tab label="Proforma Invoices" />
              </Tabs>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
            {viewMode === "list"
              ? renderInvoiceTable(invoices.tax)
              : renderInvoiceCards(invoices.tax)}
          </Box>
          <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
            {viewMode === "list"
              ? renderInvoiceTable(invoices.proforma, true)
              : renderInvoiceCards(invoices.proforma, true)}
          </Box>
        </Paper>
      </Container>
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: "fixed", bottom: 30, right: 30 }}
        onClick={handleNavigate}
      >
        <AddIcon />
      </Fab>
      {renderInvoiceDetailModal()}
    </div>
  );
}
